import React, { useEffect, useMemo, useState } from 'react';
import { GraphQlRequestService } from '../../api/graphql-request-service';
import { DashboardTracker } from '../analytics/trackers/dashboardTracker';
import { FilterTracker } from '../analytics/trackers/filterTracker';
import { SidebarTracker } from '../analytics/trackers/sidebarTracker';
import { TimeSliderTracker } from '../analytics/trackers/timeSliderTracker';
import { AliasService, BackendAliasService } from '../api/alias/service';
import { ApiAlias } from '../api/alias/types';
import {
  EffectiveRoleViewAuthorizationService,
  EffectiveRoleViewAuthorizationServiceImpl,
} from '../api/authorization/service';
import { BackendChappiService } from '../api/chappi/service';
import { DomainViewDashboardService } from '../api/dashboards/domain-view-dashboard-service';
import { FullViewDashboardService } from '../api/dashboards/full-view-dashboard-service';
import { DataUploadService } from '../api/dataupload/service';
import { useDomainDependencies } from '../api/domaindependencies/hooks';
import {
  DomainDependenciesService,
  GraphQlDomainDependenciesService,
} from '../api/domaindependencies/services';
import { DomainDependencies } from '../api/domaindependencies/types';
import { BackendDomainPreferencesService } from '../api/domainpreferences/service';
import { BackendDomainSettingsService } from '../api/domainsettings/service';
import { FeatureAccessService } from '../api/featureaccess/service';
import { FeatureFlagsService } from '../api/featureflags/service';
import { Hasher } from '../api/hasher';
import { GraphQlIpAddressService } from '../api/ipaddress/graphql-ipaddress-service';
import { BackendMetricsService } from '../api/metrics/metrics-service';
import { GraphQlNotificationService } from '../api/notifications/graphql-notification-service';
import { GraphQlPermissionService } from '../api/permission/graphql-permissions-service';
import { hashString, Role, VersionId } from '../api/types';
import { BetterLoadingOverlay } from '../common/components/BetterLoading';
import {
  DashboardPages,
  Dashboards,
} from '../common/components/dashboards/types';
import { DomainScopedErrorTracker } from '../common/components/errors/trackers/DomainScopedErrorTracker';
import { Domains, Languages } from '../constants';
import { AllUsedFields, DataTypes } from '../types';
import {
  AliasesContext,
  AliasesUpdaterContext,
  AliasServiceContext,
  AllowedDomainContext,
  AllUsedFieldsContext,
  AvailableDashboardsContext,
  ChappiServiceContext,
  DashboardTrackerContext,
  DataUploadServiceContext,
  DomainDependenciesContext,
  DomainDependenciesServiceContext,
  DomainPreferencesServiceContext,
  DomainScopedErrorTrackerContext,
  DomainSettingsServiceContext,
  DomainViewDashboardServiceContext,
  EffectivePermissionsContext,
  EffectiveRoleViewAuthorizationServiceContext,
  ExecutorRoleFromLocalStorageContext,
  ExecutorRolesContext,
  FeatureAccessServiceContext,
  FeatureFlagsServiceContext,
  FilterTrackerContext,
  FullViewDashboardServiceContext,
  IpAddressServiceContext,
  LatestEmployeeVersionContext,
  LatestPreferencesContext,
  LatestPreferencesUpdaterContext,
  LatestSettingsContext,
  LatestSettingsUpdaterContext,
  LatestVersionsContext,
  MetricsServiceContext,
  NotificationServiceContext,
  PermissionServiceContext,
  SidebarTrackerContext,
  TimeSliderTrackerContext,
  useAllowedDomainsContext,
  useAuthUserContext,
  useBaseAuthedTrackerContext,
  useDomainContext,
  useEffectiveRoleContext,
  useEnvironmentServiceContext,
  useFrontendStartupContext,
  useGlobalLocaleContext,
  useGraphQlRequestServiceContext,
  useLocalStorageCurrentUserServiceContext,
  useLocalStorageDomainServiceContext,
  useLocalStorageExecutorRoleServiceContext,
  useMixpanelContext,
  useOriginalRoleViewAuthorizationServiceContext,
  UserRolesContext,
  useSimulatedRoleContext,
} from './contexts';
import { useMemoWithDeps } from './hooks';

const DebugPrefix = 'DomainScopedContextProvider';

const DomainDependenciesLoader = ({
  children,
}: {
  children: (
    domainDependencies: DomainDependencies,
    graphQlRequestService: GraphQlRequestService,
    graphQlPermissionService: GraphQlPermissionService,
    domainScopedErrorTracker: DomainScopedErrorTracker,
    domainDependenciesService: DomainDependenciesService
  ) => React.ReactNode;
}) => {
  const authUser = useAuthUserContext();
  const domain = useDomainContext();
  const simulatedRole = useSimulatedRoleContext();
  const localStorageExecutorRoleService =
    useLocalStorageExecutorRoleServiceContext();
  const environmentService = useEnvironmentServiceContext();
  const effectiveRole = useEffectiveRoleContext();
  const localStorageDomainService = useLocalStorageDomainServiceContext();
  const localStorageCurrentUserService =
    useLocalStorageCurrentUserServiceContext();

  const domainScopedErrorTracker = useMemoWithDeps(
    `${DebugPrefix} - domainScopedErrorTracker`,
    () =>
      new DomainScopedErrorTracker(
        environmentService,
        authUser,
        domain,
        simulatedRole,
        localStorageCurrentUserService,
        localStorageDomainService,
        localStorageExecutorRoleService
      ),
    [hashString([authUser.uid, Hasher.hash(simulatedRole)])],
    [
      environmentService,
      localStorageCurrentUserService,
      localStorageExecutorRoleService,
      localStorageDomainService,
    ]
  );

  const graphQlRequestService = useGraphQlRequestServiceContext();
  graphQlRequestService.updateErrorTracker(domainScopedErrorTracker);

  const graphQlPermissionService: GraphQlPermissionService = useMemoWithDeps(
    `${DebugPrefix} - GraphQlPermissionService`,
    () =>
      new GraphQlPermissionService(
        graphQlRequestService,
        simulatedRole?.id || null
      ),
    [Hasher.hash(simulatedRole)],
    [graphQlRequestService]
  );

  const domainDependenciesService = useMemoWithDeps(
    `${DebugPrefix} - domainDependenciesService`,
    () =>
      new GraphQlDomainDependenciesService(
        graphQlRequestService,
        domain,
        effectiveRole
      ),
    [domain],
    [graphQlRequestService]
  );

  const { data } = useDomainDependencies(domainDependenciesService);

  if (!data) {
    throw new Error('Domain dependencies are undefined');
  }
  return (
    <>
      {children(
        data,
        graphQlRequestService,
        graphQlPermissionService,
        domainScopedErrorTracker,
        domainDependenciesService
      )}
    </>
  );
};

export const DomainScopedContextProviderInner = ({
  children,
  graphQlRequestService,
  graphQlPermissionService,
  domainDependencies,
  domainScopedErrorTracker,
  domainDependenciesService,
}: {
  children: React.ReactNode;
  graphQlRequestService: GraphQlRequestService;
  graphQlPermissionService: GraphQlPermissionService;
  domainDependencies: DomainDependencies;
  domainScopedErrorTracker: DomainScopedErrorTracker;
  domainDependenciesService: DomainDependenciesService;
}) => {
  console.log2('DomainScopedContextProvider start');
  const domain = useDomainContext();
  const baseAuthedTracker = useBaseAuthedTrackerContext();
  const simulatedRole = useSimulatedRoleContext();
  const locale = useGlobalLocaleContext();
  const environmentService = useEnvironmentServiceContext();
  const allowedDomains = useAllowedDomainsContext();
  const frontendStartup = useFrontendStartupContext();
  const originalRoleViewAuthorizationService =
    useOriginalRoleViewAuthorizationServiceContext();
  const localStorageExecutorRoleService =
    useLocalStorageExecutorRoleServiceContext();

  const effectivePermissions =
    frontendStartup.roleDetails.allPermissions?.validFor(domain);

  // Performance optimization (ENG-8794): Hash large objects once and reuse
  // domainDependencies is static after load, only changes on domain/role switch
  const domainDependenciesHash = Hasher.hash(domainDependencies);

  const executorRoleFromLocalStorage = useMemoWithDeps(
    `${DebugPrefix} - executorRoleFromLocalStorage`,
    () => localStorageExecutorRoleService.get(),
    [domain]
  );

  const authorizationService: EffectiveRoleViewAuthorizationService =
    useMemoWithDeps(
      `${DebugPrefix} - EffectiveRoleViewAuthorizationService`,
      () =>
        new EffectiveRoleViewAuthorizationServiceImpl(
          domain,
          effectivePermissions
        ),
      [domain, Hasher.hash(effectivePermissions)]
    );

  const latestVersions: Partial<Record<DataTypes, VersionId>> = useMemoWithDeps(
    `${DebugPrefix} - latestVersions from domainDependencies`,
    () =>
      domainDependencies.domainInfo?.allLatestVersions?.reduce<
        Partial<Record<DataTypes, VersionId>>
      >((acc, v) => {
        return v.version
          ? { ...acc, [v.dataType as unknown as DataTypes]: v.version }
          : acc;
      }, {}) ?? {},
    [domainDependenciesHash]
  );

  if (!latestVersions) {
    throw new Error('Latest versions are undefined');
  }

  const latestEmployeeVersion = useMemoWithDeps(
    `${DebugPrefix} - latestEmployeeVersion`,
    () => latestVersions[DataTypes.EMPLOYEE],
    [JSON.stringify(latestVersions)]
  );
  if (!latestEmployeeVersion) {
    throw new Error('Latest version for data type employee is undefined');
  }

  const allowedDomain = useMemoWithDeps(
    `${DebugPrefix} - allowedDomain`,
    () => allowedDomains.find((d) => d.domain === domain),
    [domain, Hasher.hash(allowedDomains)]
  );
  if (!allowedDomain) {
    throw new Error(`Domain ${domain} not found in allowed domains`);
  }

  const [latestPreferences, updateLatestPreferences] = useState(
    allowedDomain.latestPreferences?.settings
  );
  if (!latestPreferences) {
    throw new Error(`No latest preferences found for domain ${domain}`);
  }

  const [latestSettings, updateLatestSettings] = useState(
    allowedDomain.latestSettings?.settings
  );
  if (!latestSettings) {
    throw new Error(`No latest settings found for domain ${domain}`);
  }

  // Performance optimization (ENG-8794): Memoize hashes of stateful preferences/settings
  // These CAN change during session via mutation callbacks, so recompute only when they change
  const latestPreferencesHash = useMemo(
    () => Hasher.hash(latestPreferences),
    [latestPreferences]
  );
  const latestSettingsHash = useMemo(
    () => Hasher.hash(latestSettings),
    [latestSettings]
  );

  const allUsedFields = useMemoWithDeps(
    `${DebugPrefix} - allUsedFields from domainDependencies`,
    () =>
      domainDependencies.domainInfo?.allUsedFields as unknown as AllUsedFields,
    [domainDependenciesHash]
  );
  const userRoles = useMemoWithDeps(
    `${DebugPrefix} - userRoles from domainDependencies`,
    () => domainDependencies?.domainInfo?.userRoles as unknown as Role[],
    [domainDependenciesHash]
  );

  const metricsService = useMemoWithDeps(
    `${DebugPrefix} - metricsService`,
    () => new BackendMetricsService(domainDependencies),
    [domainDependenciesHash]
  );

  const aliasService: AliasService = useMemoWithDeps(
    `${DebugPrefix} - aliasService`,
    () => new BackendAliasService(graphQlRequestService, domain, simulatedRole),
    [domain, Hasher.hash(simulatedRole)],
    [graphQlRequestService]
  );

  const allDashboards = useMemoWithDeps(
    `${DebugPrefix} - allDashboards from frontendStartup`,
    () => frontendStartup.dashboards ?? [],
    [Hasher.hash(frontendStartup.dashboards)]
  );

  const dashboardTracker = useMemoWithDeps(
    `${DebugPrefix} - dashboardTracker`,
    () => new DashboardTracker(baseAuthedTracker, domain),
    [domain],
    [baseAuthedTracker]
  );
  const timeSliderTracker = useMemoWithDeps(
    `${DebugPrefix} - timeSliderTracker`,
    () => new TimeSliderTracker(baseAuthedTracker, domain),
    [domain],
    [baseAuthedTracker]
  );
  const filterTracker = useMemoWithDeps(
    `${DebugPrefix} - filterTracker`,
    () => new FilterTracker(baseAuthedTracker, domain),
    [domain],
    [baseAuthedTracker]
  );
  const sidebarTracker = useMemoWithDeps(
    `${DebugPrefix} - sidebarTracker`,
    () => new SidebarTracker(baseAuthedTracker, domain),
    [domain],
    [baseAuthedTracker]
  );

  const availableDashboards = useMemoWithDeps(
    `${DebugPrefix} - availableDashboards`,
    () => {
      // This is a temporary function
      const getAttritionTranslationKey = (
        domain: Domains,
        selectedLanguage: Languages,
        baseKey: string
      ) => {
        const splitBaseKey = baseKey.split('.');
        const newSplitKey =
          domain === Domains.NIKKI && selectedLanguage === Languages.EN
            ? [...splitBaseKey.front(), 'nikki', splitBaseKey.last() as string]
            : splitBaseKey;
        return newSplitKey.join('.');
      };
      return {
        dashboards: allDashboards.map((d) => ({
          ...d,
          imageLink: d.imagePath,
          nameKey:
            d.id === Dashboards.ATTRITION
              ? getAttritionTranslationKey(domain, locale.selected, d.nameKey)
              : d.nameKey,
          pages: d.pages.map((p) => ({
            ...p,
            dashboardId: d.id,
            imageLink: d.imagePath,
            nameKey:
              p.pageId === DashboardPages.ATTRITION_OVERVIEW ||
              p.pageId === DashboardPages.GENDER_DIVERSITY_ATTRITION
                ? getAttritionTranslationKey(domain, locale.selected, p.nameKey)
                : p.nameKey,
          })),
          connectMoreToolsLink: '',
        })),
      };
    },
    [...allDashboards.map((d) => Hasher.hash(d)), domain, locale.selected]
  );

  const fullViewDashboardService: FullViewDashboardService = useMemoWithDeps(
    `${DebugPrefix} - fullViewDashboardService`,
    () => new FullViewDashboardService(availableDashboards, latestPreferences),
    [
      hashString([
        Hasher.hash(availableDashboards.dashboards),
        latestPreferencesHash,
      ]),
    ]
  );

  const domainPreferencesService = useMemoWithDeps(
    `${DebugPrefix} - domainPreferencesService`,
    () =>
      new BackendDomainPreferencesService(graphQlRequestService, simulatedRole),
    [Hasher.hash(simulatedRole)],
    [graphQlRequestService]
  );

  const domainSettingsService = useMemoWithDeps(
    `${DebugPrefix} - domainSettingsService`,
    () =>
      new BackendDomainSettingsService(graphQlRequestService, simulatedRole),
    [Hasher.hash(simulatedRole)],
    [graphQlRequestService]
  );

  const domainViewDashboardService: DomainViewDashboardService =
    useMemoWithDeps(
      `${DebugPrefix} - domainViewDashboardService`,
      () =>
        new DomainViewDashboardService(
          fullViewDashboardService,
          latestPreferences,
          latestSettings,
          domain,
          environmentService
        ),
      [hashString([latestPreferencesHash, latestSettingsHash, domain])],
      [fullViewDashboardService]
    );

  const featureFlagsService = useMemoWithDeps(
    `${DebugPrefix} - featureFlagsService`,
    () =>
      new FeatureFlagsService(
        authorizationService,
        latestSettings,
        environmentService,
        originalRoleViewAuthorizationService
      ),
    [latestSettingsHash],
    [
      authorizationService,
      environmentService,
      originalRoleViewAuthorizationService,
    ]
  );

  const featureAccessService = useMemoWithDeps(
    `${DebugPrefix} - featureAccessService`,
    () => new FeatureAccessService(featureFlagsService, authorizationService),
    [],
    [featureFlagsService, authorizationService]
  );

  const aliasesFromDomainDependencies = (domainDependencies.domainInfo
    ?.aliases ?? []) as ApiAlias[];
  const [aliases, setAliases] = useState<ApiAlias[] | null>(
    aliasesFromDomainDependencies
  );

  const chappiService = useMemoWithDeps(
    `${DebugPrefix} - chappiService`,
    () =>
      new BackendChappiService(graphQlRequestService, domain, simulatedRole),
    [domain, Hasher.hash(simulatedRole)],
    [graphQlRequestService]
  );

  const notificationService = useMemoWithDeps(
    `${DebugPrefix} - notificationService`,
    () =>
      new GraphQlNotificationService(
        graphQlRequestService,
        domain,
        simulatedRole?.id ?? null
      ),
    [domain, Hasher.hash(simulatedRole)],
    [graphQlRequestService]
  );

  const ipAddressService = useMemoWithDeps(
    `${DebugPrefix} - ipAddressService`,
    () =>
      new GraphQlIpAddressService(
        graphQlRequestService,
        simulatedRole?.id ?? null
      ),
    [Hasher.hash(simulatedRole)],
    [graphQlRequestService]
  );

  const dataUploadService = useMemoWithDeps(
    `${DebugPrefix} - dataUploadService`,
    () =>
      new DataUploadService(
        graphQlRequestService,
        authorizationService,
        environmentService,
        simulatedRole?.id ?? null
      ),
    [Hasher.hash(simulatedRole)],
    [graphQlRequestService, authorizationService, environmentService]
  );

  const executorRoles = useMemoWithDeps(
    `${DebugPrefix} - executorRoles`,
    () => domainDependencies.executorRoles,
    [domainDependenciesHash],
    []
  );

  const mixpanel = useMixpanelContext();
  useEffect(() => {
    mixpanel.actions.register({ domain });
  }, [mixpanel, domain]);

  if (aliases === null) {
    return (
      <BetterLoadingOverlay
        debugString="DomainDependenciesAliasesLoader"
        isPending={aliases === null}
      />
    );
  }

  return (
    <FeatureAccessServiceContext.Provider value={featureAccessService}>
      <FeatureFlagsServiceContext.Provider value={featureFlagsService}>
        <ExecutorRolesContext.Provider value={executorRoles}>
          <EffectivePermissionsContext.Provider value={effectivePermissions}>
            <EffectiveRoleViewAuthorizationServiceContext.Provider
              value={authorizationService}
            >
              <PermissionServiceContext.Provider
                value={graphQlPermissionService}
              >
                <LatestPreferencesContext.Provider value={latestPreferences}>
                  <LatestPreferencesUpdaterContext.Provider
                    value={updateLatestPreferences}
                  >
                    <LatestSettingsContext.Provider value={latestSettings}>
                      <LatestSettingsUpdaterContext.Provider
                        value={updateLatestSettings}
                      >
                        <DomainDependenciesContext.Provider
                          value={domainDependencies}
                        >
                          <UserRolesContext.Provider value={userRoles}>
                            <AliasesContext.Provider value={aliases}>
                              <AliasesUpdaterContext.Provider
                                value={setAliases}
                              >
                                <AllUsedFieldsContext.Provider
                                  value={allUsedFields}
                                >
                                  <LatestEmployeeVersionContext.Provider
                                    value={latestEmployeeVersion}
                                  >
                                    <LatestVersionsContext.Provider
                                      value={latestVersions}
                                    >
                                      <MetricsServiceContext.Provider
                                        value={metricsService}
                                      >
                                        <DomainScopedErrorTrackerContext.Provider
                                          value={domainScopedErrorTracker}
                                        >
                                          <DomainSettingsServiceContext.Provider
                                            value={domainSettingsService}
                                          >
                                            <DomainPreferencesServiceContext.Provider
                                              value={domainPreferencesService}
                                            >
                                              <AvailableDashboardsContext.Provider
                                                value={
                                                  availableDashboards.dashboards
                                                }
                                              >
                                                <AllowedDomainContext.Provider
                                                  value={allowedDomain}
                                                >
                                                  <FullViewDashboardServiceContext.Provider
                                                    value={
                                                      fullViewDashboardService
                                                    }
                                                  >
                                                    <DomainViewDashboardServiceContext.Provider
                                                      value={
                                                        domainViewDashboardService
                                                      }
                                                    >
                                                      <AliasServiceContext.Provider
                                                        value={aliasService}
                                                      >
                                                        <DashboardTrackerContext.Provider
                                                          value={
                                                            dashboardTracker
                                                          }
                                                        >
                                                          <SidebarTrackerContext.Provider
                                                            value={
                                                              sidebarTracker
                                                            }
                                                          >
                                                            <TimeSliderTrackerContext.Provider
                                                              value={
                                                                timeSliderTracker
                                                              }
                                                            >
                                                              <FilterTrackerContext.Provider
                                                                value={
                                                                  filterTracker
                                                                }
                                                              >
                                                                <ChappiServiceContext.Provider
                                                                  value={
                                                                    chappiService
                                                                  }
                                                                >
                                                                  <NotificationServiceContext.Provider
                                                                    value={
                                                                      notificationService
                                                                    }
                                                                  >
                                                                    <IpAddressServiceContext.Provider
                                                                      value={
                                                                        ipAddressService
                                                                      }
                                                                    >
                                                                      <DataUploadServiceContext.Provider
                                                                        value={
                                                                          dataUploadService
                                                                        }
                                                                      >
                                                                        <DomainDependenciesServiceContext.Provider
                                                                          value={
                                                                            domainDependenciesService
                                                                          }
                                                                        >
                                                                          <ExecutorRoleFromLocalStorageContext.Provider
                                                                            value={
                                                                              executorRoleFromLocalStorage
                                                                            }
                                                                          >
                                                                            {
                                                                              children
                                                                            }
                                                                          </ExecutorRoleFromLocalStorageContext.Provider>
                                                                        </DomainDependenciesServiceContext.Provider>
                                                                      </DataUploadServiceContext.Provider>
                                                                    </IpAddressServiceContext.Provider>
                                                                  </NotificationServiceContext.Provider>
                                                                </ChappiServiceContext.Provider>
                                                              </FilterTrackerContext.Provider>
                                                            </TimeSliderTrackerContext.Provider>
                                                          </SidebarTrackerContext.Provider>
                                                        </DashboardTrackerContext.Provider>
                                                      </AliasServiceContext.Provider>
                                                    </DomainViewDashboardServiceContext.Provider>
                                                  </FullViewDashboardServiceContext.Provider>
                                                </AllowedDomainContext.Provider>
                                              </AvailableDashboardsContext.Provider>
                                            </DomainPreferencesServiceContext.Provider>
                                          </DomainSettingsServiceContext.Provider>
                                        </DomainScopedErrorTrackerContext.Provider>
                                      </MetricsServiceContext.Provider>
                                    </LatestVersionsContext.Provider>
                                  </LatestEmployeeVersionContext.Provider>
                                </AllUsedFieldsContext.Provider>
                              </AliasesUpdaterContext.Provider>
                            </AliasesContext.Provider>
                          </UserRolesContext.Provider>
                        </DomainDependenciesContext.Provider>
                      </LatestSettingsUpdaterContext.Provider>
                    </LatestSettingsContext.Provider>
                  </LatestPreferencesUpdaterContext.Provider>
                </LatestPreferencesContext.Provider>
              </PermissionServiceContext.Provider>
            </EffectiveRoleViewAuthorizationServiceContext.Provider>
          </EffectivePermissionsContext.Provider>
        </ExecutorRolesContext.Provider>
      </FeatureFlagsServiceContext.Provider>
    </FeatureAccessServiceContext.Provider>
  );
};

export const DomainScopedContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <DomainDependenciesLoader>
      {(
        domainDependencies,
        graphQlRequestService,
        graphQlPermissionService,
        domainScopedErrorTracker,
        domainDependenciesService
      ) => {
        return (
          <DomainScopedContextProviderInner
            graphQlRequestService={graphQlRequestService}
            graphQlPermissionService={graphQlPermissionService}
            domainDependencies={domainDependencies}
            domainScopedErrorTracker={domainScopedErrorTracker}
            domainDependenciesService={domainDependenciesService}
          >
            {children}
          </DomainScopedContextProviderInner>
        );
      }}
    </DomainDependenciesLoader>
  );
};
